import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useShare from '../../Share/components/hooks/useShare';
import useActivation, { ActivationObject } from './hooks/useActivation';
import GalleryList from '../../GalleryConfiguration/components/GalleryList';
import Loading from '../../Common/components/Loading';
import MediaStatsTable from './MediaStatsTable';
import ShareJourneyStats from './ShareJourneyStats';
import ShareStatsTable from './ShareStatsTable';
import SessionStatsTable from './SessionStatsTable';
import { Tabs, TabsContent } from '@/src/shadComponents/ui/tabs';
import Processing from '@/src/GalleryConfiguration/components/Processing';
import { Link } from 'react-router-dom';
//import ActivationStats from './ActivationStats';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from '@/src/shadComponents/ui/card';
import useJobHelpers from '@/src/Jobs/components/hooks/useJobHelpers';
import ActivationDocuments from './ActivationDocuments';
import PageWithSidebar from '@/src/Common/components/PageWithSidebar';
import {
    DocumentCheckIcon,
    Cog6ToothIcon,
    AdjustmentsHorizontalIcon,
    ChartBarIcon,
    PhotoIcon,
    ShareIcon,
    SparklesIcon,
    BeakerIcon,
    EllipsisVerticalIcon,
    QrCodeIcon,
    BoltIcon,
    CreditCardIcon,
    LinkIcon,
    ArrowsRightLeftIcon,
} from '@heroicons/react/20/solid';
import { Menu, Transition } from '@headlessui/react';
import PhotoManager from './PhotoManager';
import FeatureFlag from '@/src/Features/components/FeatureFlag';
import { FeatureFlags } from '@/src/Features/lib/constants';
import ShareManager from './ShareManager';
import useGetGalleries from '@/src/GalleryConfiguration/components/hooks/useGetGalleries';
import ExperienceBuilder from '@/src/ExperienceBuilder/ExperienceBuilder';
import useFeatureFlags from '@/src/Features/components/hooks/useFeatureFlags';
import { ActivationQueueData } from './ActivationQueueData';
import useWhitelabelSettings from '@/src/WhitelabelSettings/components/hooks/useWhitelabelSettings';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import RekognitionSettings from './RekognitionSettings';
import RekognitionLabels from './RekognitionLabels';
import ReassignOrgModal from './ReassignOrgModal';
import ReassignmentSuccessModal from './ReassignmentSuccessModal';
import ActionCodeBuilder from './ActionCodeBuilder';
import QrAutomationSettings from './QrAutomationSettings';
import WorkflowBuilder from './WorkflowBuilder/WorkflowBuilder';
import PaymentSettings from './PaymentSettings';
import WebhookSettings from './WebhookSettings';

export default function ActivationDashboard() {
    const navigate = useNavigate();
    const { activationId: id } = useParams();
    const [galleries, setGalleries] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const {
        getActivation,
        getActivationMediaStats,
        getActivationSessions,
        unarchiveActivation,
    } = useActivation();
    const { getSharesForActivation, getShareStatsForActivation } = useShare();
    const [, setShares] = useState<any[]>([]);
    const [shareStats, setShareStats] = useState<any[]>([]);
    const [mediaStats, setMediaStats] = useState<any[]>([]);
    const [sessions, setSessions] = useState<any[]>([]);
    const [job, setJob] = useState<any>(null);
    const { getJob } = useJobHelpers();
    const { getGalleriesForActivation } = useGetGalleries();
    const { getOrgDomain } = useWhitelabelSettings();
    const [galleryUrl, setGalleryUrl] = useState<string | null>(null);
    const { org } = useUserOrg();

    const [activation, setActivation] = useState<ActivationObject | null>(null);
    const { featureFlags } = useFeatureFlags();
    const [showReassignOrgModal, setShowReassignOrgModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [reassignmentData, setReassignmentData] = useState<{
        newOrgId: number;
        newOrgName: string;
        oldOrgName: string;
    } | null>(null);

    const handleUnarchive = async () => {
        if (!activation) return;

        const shouldUnarchive = window.confirm(
            `Are you sure you want to unarchive ${activation.name}?`,
        );

        if (shouldUnarchive) {
            const success = await unarchiveActivation(activation.id);
            if (success) {
                // Refresh the activation data
                const updatedActivation = await getActivation(activation.id);
                setActivation(updatedActivation);
            }
        }
    };

    useEffect(() => {
        let isMounted = true;
        const getData = async () => {
            try {
                const activationId = id ? +id : 0;
                const activation = await getActivation(activationId);

                if (activation.jobId) {
                    const job = await getJob(activation.jobId);
                    setJob(job);
                }

                const shares = await getSharesForActivation(activationId);
                const shareStats = await getShareStatsForActivation(
                    activationId,
                );

                const mediaStats = await getActivationMediaStats(activationId);

                const sessions = await getActivationSessions(activationId);

                const galleries = await getGalleriesForActivation(activationId);

                const galleryUrl = await getOrgDomain(org?.id);

                setActivation(activation);
                setShares(shares);
                setShareStats(shareStats);
                setMediaStats(mediaStats);
                setSessions(sessions);
                setGalleries(galleries);
                setGalleryUrl(galleryUrl);

                setTimeout(() => isMounted && setIsLoading(false), 0);
            } catch (error) {
                console.error(error);
            }
        };

        getData();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    let splitPath = window.location.href.split('#');
    let selectedTab = splitPath.length > 1 ? splitPath[1] : 'details';

    const navigation = useMemo(() => {
        var nav = [
            {
                name: 'Details',
                href: '#details',
                current: selectedTab === 'details',
                icon: DocumentCheckIcon,
            },
            {
                name: 'Gallery Configuration',
                href: '#galleries',
                current: selectedTab === 'galleries',
                icon: Cog6ToothIcon,
            },
            {
                name: 'Media',
                href: '#media',
                current: selectedTab === 'media',
                icon: PhotoIcon,
            },
            {
                name: 'Processing',
                href: '#processing',
                current: selectedTab === 'processing',
                icon: AdjustmentsHorizontalIcon,
            },
            {
                name: 'Shares',
                href: '#shares',
                current: selectedTab === 'shares',
                icon: ShareIcon,
            },
            {
                name: 'Stats',
                href: '#stats',
                current: selectedTab === 'stats',
                icon: ChartBarIcon,
            },
            {
                name: 'Analyze',
                href: '#analyze',
                current: selectedTab === 'analyze',
                icon: BeakerIcon,
            },
            {
                name: 'Clickerbooth Code',
                href: '#code',
                current: selectedTab === 'code',
                icon: QrCodeIcon,
            },
            {
                name: 'QR Automation',
                href: '#qr-automation',
                current: selectedTab === 'qr-automation',
                icon: BoltIcon,
            },
            {
                name: 'Workflows',
                href: '#workflow',
                current: selectedTab === 'workflow',
                icon: ArrowsRightLeftIcon,
            },
            {
                name: 'Payments',
                href: '#payments',
                current: selectedTab === 'payments',
                icon: CreditCardIcon,
            },
            {
                name: 'Integrations',
                href: '#integrations',
                current: selectedTab === 'integrations',
                icon: LinkIcon,
            },
        ];

        if (featureFlags[FeatureFlags.EXPERIENCE_BUILDER_V1]) {
            nav.push({
                name: 'Experience Builder',
                href: '#experience',
                current: selectedTab === 'experience',
                icon: SparklesIcon,
            });
        }

        return nav;

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [featureFlags, selectedTab]);

    if (isLoading) {
        return <Loading isLoading={true} />;
    }

    if (!activation) {
        return <div></div>;
    }

    function classNames(...classes: string[]) {
        return classes.filter(Boolean).join(' ');
    }

    const actions = (
        <Menu as="div" className="relative flex-none">
            <Menu.Button className="-m-2.5 block p-2.5 text-gray-500 hover:text-gray-900">
                <span className="sr-only">Open options</span>
                <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
            </Menu.Button>
            <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
            >
                <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                    <Menu.Item>
                        {({ active }) => (
                            <button
                                onClick={() =>
                                    navigate(`/activations/${id}/edit`)
                                }
                                className={classNames(
                                    active ? 'bg-gray-50' : '',
                                    'block w-full px-3 py-1 text-left text-sm leading-6 text-gray-900',
                                )}
                            >
                                Edit Details
                            </button>
                        )}
                    </Menu.Item>
                    <FeatureFlag flag={FeatureFlags.ACTIVATION_ORG_SWAP_V1}>
                        <Menu.Item>
                            {({ active }) => (
                                <button
                                    onClick={() =>
                                        setShowReassignOrgModal(true)
                                    }
                                    className={classNames(
                                        active ? 'bg-gray-50' : '',
                                        'block w-full px-3 py-1 text-left text-sm leading-6 text-gray-900',
                                    )}
                                >
                                    Reassign Organization
                                </button>
                            )}
                        </Menu.Item>
                    </FeatureFlag>
                    {activation?.isArchived && (
                        <Menu.Item>
                            {({ active }) => (
                                <button
                                    onClick={handleUnarchive}
                                    className={classNames(
                                        active ? 'bg-gray-50' : '',
                                        'block w-full px-3 py-1 text-left text-sm leading-6 text-gray-900',
                                    )}
                                >
                                    Unarchive
                                </button>
                            )}
                        </Menu.Item>
                    )}
                </Menu.Items>
            </Transition>
        </Menu>
    );

    const title = (
        <div className="flex items-center gap-2">
            <span>{activation.name}</span>
            {activation.isArchived && (
                <span className="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/10">
                    Archived
                </span>
            )}
        </div>
    );

    return (
        <PageWithSidebar
            title={title}
            subtitle={job?.name}
            actions={actions}
            navigation={navigation}
        >
            {/*selectedTab !== 'media' ? <ActivationStats /> : null*/}
            <Tabs value={`${selectedTab}`}>
                <TabsContent value="galleries">
                    <GalleryList activationId={id ? +id : 0} />
                </TabsContent>
                <TabsContent value="processing">
                    <Processing activationId={id ? +id : 0} />
                </TabsContent>
                <TabsContent value="media">
                    <PhotoManager />
                </TabsContent>
                <FeatureFlag flag={FeatureFlags.EXPERIENCE_BUILDER_V1}>
                    <TabsContent value="experience">
                        <ExperienceBuilder />
                    </TabsContent>
                </FeatureFlag>
                <TabsContent value="details">
                    <div className="grid px-2">
                        <CardHeader>
                            <CardTitle>Activation Details</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="">
                                <span className="label">Date: </span>
                                <span>{activation.date.toString()}</span>
                            </div>
                            <div className="Block-row">
                                <span className="label">Owner: </span>
                                <span>{activation.owner}</span>
                            </div>
                            <div className="Block-row">
                                <span className="label">Location: </span>
                                <span>{activation.location}</span>
                            </div>

                            {activation.v3Folder && (
                                <div className="Block-row">
                                    <span className="label">Linked To: </span>
                                    <span>{activation.v3Folder}</span>
                                </div>
                            )}

                            {galleries.map((gallery) => {
                                // Use the appropriate base URL based on environment or default to relative URL if settings not available

                                const isExternalUrl =
                                    galleryUrl && !galleryUrl.startsWith('/');

                                // Ensure the baseUrl doesn't have a trailing slash to avoid double slashes
                                const cleanGalleryUrl = galleryUrl
                                    ? galleryUrl.endsWith('/')
                                        ? galleryUrl.slice(0, -1)
                                        : galleryUrl
                                    : null;

                                const fullGalleryUrl = galleryUrl
                                    ? `${cleanGalleryUrl}/gallery/${encodeURIComponent(
                                          gallery.slug,
                                      )}`
                                    : `/gallery/${encodeURIComponent(
                                          gallery.slug,
                                      )}`;

                                const slideshowUrl = galleryUrl
                                    ? `${cleanGalleryUrl}/gallery/${encodeURIComponent(
                                          gallery.slug,
                                      )}/slideshow`
                                    : `/gallery/${encodeURIComponent(
                                          gallery.slug,
                                      )}/slideshow`;

                                const sharingUrl = galleryUrl
                                    ? `${cleanGalleryUrl}/gallery/${encodeURIComponent(
                                          gallery.slug,
                                      )}/sharing`
                                    : `/gallery/${encodeURIComponent(
                                          gallery.slug,
                                      )}/sharing`;

                                const lookupUrl = galleryUrl
                                    ? `${cleanGalleryUrl}/lookup/${encodeURIComponent(
                                          gallery.slug,
                                      )}`
                                    : `/lookup/${encodeURIComponent(
                                          gallery.slug,
                                      )}`;

                                return (
                                    <React.Fragment
                                        key={`gallery-links-${gallery.id}`}
                                    >
                                        <div className="Block-row">
                                            <span className="label">
                                                Gallery:{' '}
                                            </span>
                                            {isExternalUrl ? (
                                                <a
                                                    href={fullGalleryUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {gallery.name}
                                                    &nbsp;&nbsp;
                                                    <i className="bi bi-box-arrow-up-right"></i>
                                                </a>
                                            ) : (
                                                <Link to={fullGalleryUrl}>
                                                    {gallery.name}
                                                    &nbsp;&nbsp;
                                                    <i className="bi bi-box-arrow-up-right"></i>
                                                </Link>
                                            )}
                                        </div>

                                        <div className="Block-row">
                                            <span className="label">
                                                Slideshow:{' '}
                                            </span>
                                            {isExternalUrl ? (
                                                <a
                                                    href={slideshowUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {gallery.name}
                                                    &nbsp;&nbsp;
                                                    <i className="bi bi-box-arrow-up-right"></i>
                                                </a>
                                            ) : (
                                                <Link to={slideshowUrl}>
                                                    {gallery.name}
                                                    &nbsp;&nbsp;
                                                    <i className="bi bi-box-arrow-up-right"></i>
                                                </Link>
                                            )}
                                        </div>

                                        <div className="Block-row">
                                            <span className="label">
                                                Sharing Station:{' '}
                                            </span>
                                            {isExternalUrl ? (
                                                <a
                                                    href={sharingUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {gallery.name}
                                                    &nbsp;&nbsp;
                                                    <i className="bi bi-box-arrow-up-right"></i>
                                                </a>
                                            ) : (
                                                <Link to={sharingUrl}>
                                                    {gallery.name}
                                                    &nbsp;&nbsp;
                                                    <i className="bi bi-box-arrow-up-right"></i>
                                                </Link>
                                            )}
                                        </div>

                                        <div className="Block-row">
                                            <span className="label">
                                                Analyze Lookup:{' '}
                                            </span>
                                            {isExternalUrl ? (
                                                <a
                                                    href={lookupUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {gallery.name}
                                                    &nbsp;&nbsp;
                                                    <i className="bi bi-box-arrow-up-right"></i>
                                                </a>
                                            ) : (
                                                <Link to={lookupUrl}>
                                                    {gallery.name}
                                                    &nbsp;&nbsp;
                                                    <i className="bi bi-box-arrow-up-right"></i>
                                                </Link>
                                            )}
                                        </div>
                                    </React.Fragment>
                                );
                            })}
                        </CardContent>
                        <CardHeader></CardHeader>
                        <CardContent>
                            <CardTitle>Queue Data</CardTitle>
                            <CardDescription>
                                Devices which have uploaded to this activation
                                will appear below.
                            </CardDescription>
                            <ActivationQueueData activationId={id ? +id : 0} />
                        </CardContent>
                        <FeatureFlag flag={FeatureFlags.DOCUMENT_VIEWER_V1}>
                            <Card>
                                <CardHeader>
                                    <CardTitle>Activation Documents</CardTitle>
                                    <CardContent className="p-0">
                                        <ActivationDocuments />
                                    </CardContent>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-16 xl:space-y-20">
                                        <div>
                                            <div className="overflow-hidden border-t border-gray-100">
                                                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                                                    <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-none">
                                                        <table className="w-full text-left">
                                                            <thead className="sr-only">
                                                                <tr>
                                                                    <th></th>
                                                                    <th className="hidden sm:table-cell"></th>
                                                                    <th></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody></tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </FeatureFlag>
                    </div>
                </TabsContent>
                <TabsContent value="shares">
                    <ShareManager />
                </TabsContent>
                <TabsContent value="stats">
                    <div className="row">
                        <>
                            <div className="mx-auto flex max-w-10xl flex-wrap items-center gap-6 px-4 sm:flex-nowrap sm:px-6 lg:px-8">
                                <h1 className="mt-1 text-xl font-semibold leading-6 text-gray-900">
                                    Sessions
                                </h1>
                            </div>
                            <SessionStatsTable
                                activationId={activation.id}
                                sessionStats={sessions}
                            />
                        </>

                        {shareStats.length > 0 && (
                            <>
                                <div className="pt-10 mx-auto flex max-w-10xl flex-wrap items-center gap-6 px-4 sm:flex-nowrap sm:px-6 lg:px-8">
                                    <h1 className="mt-1 text-xl font-semibold leading-6 text-gray-900">
                                        Share Stats
                                    </h1>
                                </div>
                                <ShareStatsTable shareStats={shareStats} />
                            </>
                        )}
                        {mediaStats.length > 0 && (
                            <>
                                <div className="pt-10 mx-auto flex max-w-10xl flex-wrap items-center gap-6 px-4 sm:flex-nowrap sm:px-6 lg:px-8">
                                    <h1 className="mt-1 text-xl font-semibold leading-6 text-gray-900">
                                        Media Stats
                                    </h1>
                                </div>
                                <MediaStatsTable mediaStats={mediaStats} />
                            </>
                        )}
                        <ShareJourneyStats activationId={id ? +id : 0} />
                    </div>
                </TabsContent>
                <TabsContent value="analyze">
                    <div className="space-y-6">
                        <RekognitionSettings activationId={id ? +id : 0} />
                        <RekognitionLabels activationId={id ? +id : 0} />
                    </div>
                </TabsContent>
                <TabsContent value="code">
                    <ActionCodeBuilder activationId={id ? +id : 0} />
                </TabsContent>
                <TabsContent value="qr-automation">
                    <QrAutomationSettings />
                </TabsContent>
                <TabsContent value="workflow">
                    <WorkflowBuilder activationId={id ? +id : 0} />
                </TabsContent>
                <TabsContent value="payments">
                    <PaymentSettings />
                </TabsContent>
                <TabsContent value="integrations">
                    <WebhookSettings />
                </TabsContent>
            </Tabs>
            {showReassignOrgModal && activation && (
                <ReassignOrgModal
                    activationId={activation.id}
                    activationName={activation.name}
                    currentOrgId={activation.org_id}
                    onDismiss={() => setShowReassignOrgModal(false)}
                    onSuccess={(newOrgId, newOrgName, oldOrgName) => {
                        setReassignmentData({
                            newOrgId,
                            newOrgName,
                            oldOrgName,
                        });
                        setShowSuccessModal(true);
                    }}
                />
            )}
            {showSuccessModal && activation && reassignmentData && (
                <ReassignmentSuccessModal
                    activationId={activation.id}
                    activationName={activation.name}
                    oldOrgName={reassignmentData.oldOrgName}
                    newOrgId={reassignmentData.newOrgId}
                    newOrgName={reassignmentData.newOrgName}
                    onDismiss={() => {
                        setShowSuccessModal(false);
                        setReassignmentData(null);
                    }}
                />
            )}
        </PageWithSidebar>
    );
}
